import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import notify from "../lib/notify";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import { Button as FcButton, Card as FcCard, Input as FcInput, Select as FcSelect, Textarea as FcTextarea } from '../ui';

export default function Support({ session, userRole, username }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    category: "question",
    priority: "medium",
    subject: "",
    message: "",
  });
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (session?.user?.email) {
      setEmail(session.user.email);
    }
  }, [session]);

  const categories = [
    { value: "bug", label: "🐛 Bug Report" },
    { value: "feature", label: "💡 Feature Request" },
    { value: "question", label: "❓ Question" },
    { value: "account", label: "👤 Account Issue" },
    { value: "other", label: "📝 Other" },
  ];

  const priorities = [
    { value: "low", label: "Low - General inquiry" },
    { value: "medium", label: "Medium - Need help" },
    { value: "high", label: "High - Issue affecting usage" },
    { value: "urgent", label: "Urgent - Critical issue" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getBrowserInfo = () => {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim()) {
      notify("Please enter a subject", "error");
      return;
    }

    if (!formData.message.trim()) {
      notify("Please enter a message", "error");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.from("support_tickets").insert({
        user_id: session.user.id,
        email: email,
        username: username,
        category: formData.category,
        priority: formData.priority,
        subject: formData.subject,
        message: formData.message,
        browser_info: getBrowserInfo(),
        page_url: window.location.href,
      });

      if (error) throw error;

      notify("Support ticket submitted successfully! We'll get back to you soon.", "success");
      navigate("/my-tickets");
    } catch (error) {
      notify("Failed to submit ticket. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex">
      <SidebarNavbar
        handleLogout={async () => {
          await supabase.auth.signOut();
          navigate("/login");
        }}
        userRole={userRole}
      />
      <div role="main" id="fc-main-content" className="flex-1 md:ml-64 fc-workspace">
        <div className="min-h-screen bg-surface-canvas py-8 px-4 md:px-8 pb-20 md:pb-8">
          <div className="max-w-3xl mx-auto">
            <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-md p-6 md:p-8">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-ui-green mb-2">Support</h1>
              <p className="text-content-secondary">
                Have a question or feedback? Send us a support ticket.
              </p>
            </div>

            {/* Quick Links */}
            <div className="mb-8 p-4 bg-surface-canvas rounded-lg border border-blue-200">
              <h3 className="font-semibold text-ui-green mb-2">Before you submit:</h3>
              <ul className="text-sm text-content-primary space-y-1 ml-4">
                <li>• Check your{" "}
                  <FcButton variant="tertiary" type="submit"
                    onClick={() => navigate("/my-tickets")}
                    className="hover:underline"
                  >
                    existing tickets
                  </FcButton>
                </li>
                <li>• Review our{" "}
                  <FcButton variant="tertiary" type="submit"
                    onClick={() => navigate("/terms")}
                    className="hover:underline"
                  >
                    Terms of Service
                  </FcButton>
                </li>
                <li>• Read our{" "}
                  <FcButton variant="tertiary" type="submit"
                    onClick={() => navigate("/privacy")}
                    className="hover:underline"
                  >
                    Privacy Policy
                  </FcButton>
                </li>
              </ul>
            </div>

            {/* Support Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Info (Read-only) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ui-green mb-2">
                    Username
                  </label>
                  <FcInput aria-label="Username"
                    type="text"
                    value={username}
                    disabled
                    className="w-full border"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ui-green mb-2">
                    Email
                  </label>
                  <FcInput aria-label="Email"
                    type="email"
                    value={email}
                    disabled
                    className="w-full border"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-ui-green mb-2">
                  Category <span className="text-ui-error">*</span>
                </label>
                <FcSelect aria-label="Category *"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full border focus:border-transparent"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </FcSelect>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-ui-green mb-2">
                  Priority <span className="text-ui-error">*</span>
                </label>
                <FcSelect aria-label="Priority *"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  required
                  className="w-full border focus:border-transparent"
                >
                  {priorities.map((pri) => (
                    <option key={pri.value} value={pri.value}>
                      {pri.label}
                    </option>
                  ))}
                </FcSelect>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-ui-green mb-2">
                  Subject <span className="text-ui-error">*</span>
                </label>
                <FcInput aria-label="Brief description of your issue"
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="Brief description of your issue"
                  className="w-full border focus:border-transparent"
                  maxLength={200}
                />
                <p className="text-xs text-content-secondary mt-1">
                  {formData.subject.length}/200 characters
                </p>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-ui-green mb-2">
                  Message <span className="text-ui-error">*</span>
                </label>
                <FcTextarea aria-label="Please provide as much detail as possible..."
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Please provide as much detail as possible..."
                  rows={8}
                  className="w-full border focus:border-transparent resize-none"
                  maxLength={2000}
                />
                <p className="text-xs text-content-secondary mt-1">
                  {formData.message.length}/2000 characters
                </p>
              </div>

              {/* Info Note */}
              <div className="p-4 bg-gray-50 rounded-lg border border-ui-border">
                <p className="text-xs text-content-secondary">
                  ℹ️ Technical information (browser, screen size, etc.) will be automatically
                  included to help us diagnose issues faster.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <FcButton variant="tertiary"
                  type="submit"
                  disabled={loading}
                  className="flex-1 disabled:cursor-not-allowed"
                >
                  {loading ? "Submitting..." : "Submit Ticket"}
                </FcButton>
                <FcButton variant="secondary"
                  type="button"
                  onClick={() => navigate("/my-tickets")}
                  className="flex-1 border-2"
                >
                  View My Tickets
                </FcButton>
              </div>
            </form>
          </FcCard>

          {/* Contact Info Footer */}
          <div className="mt-6 text-center text-sm text-content-secondary">
            <p>
              For urgent matters, email us directly at{" "}
              <a
                href="mailto:support@myfootycoach.com"
                className="text-action-primary hover:underline font-medium"
              >
                support@myfootycoach.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
    <BottomNavbar
      userRole={userRole}
      username={username}
      handleLogout={async () => {
        await supabase.auth.signOut();
        navigate("/login");
      }}
    />
  </div>
);
}
